/**
 * ResponsiveScaleContext
 *
 * This module provides a React Context system for managing responsive scaling
 * throughout the application. It allows components to access and update a
 * scale factor that helps maintain proper proportions when rendering content
 * at different screen sizes.
 *
 * The context includes both the current scale value and a setter function
 * to allow components to read or modify the scale as needed.
 */
import { createContext, useContext } from 'react';
import { SharedValue } from 'react-native-reanimated';

/**
 * Interface defining the shape of the ResponsiveScaleContext
 * @property {number} responsiveScale - The current scale factor to apply to content (React value)
 * @property {number} containerResponsiveScale - The current scale factor to apply to content (React value)
 * @property {SharedValue<number>} responsiveScaleSV - SharedValue version for animations
 * @property {SharedValue<number>} containerResponsiveScaleSV - SharedValue version for animations
 */
export interface ResponsiveScaleContextProps {
  responsiveScale: number;
  containerResponsiveScale: number;
  responsiveScaleSV: SharedValue<number>;
  containerResponsiveScaleSV: SharedValue<number>;
  scaleValue: (value: number) => number;
  unscaleValue: (value: number) => number;
  scaleObject: <T extends object>(obj: T) => T;
  unscaleObject: <T extends object>(obj: T) => T;
}

/**
 * Create a context for responsive scaling with default values.
 * The default implementation provides:
 * - Default scales of 0 (which should be overridden by the provider)
 * - Default SharedValues (temporary placeholders)
 * - Error functions if called outside a provider
 */
export const ResponsiveScaleContext = createContext<ResponsiveScaleContextProps>({
  responsiveScale: 0,
  containerResponsiveScale: 0,
  responsiveScaleSV: { value: 0 } as SharedValue<number>,
  containerResponsiveScaleSV: { value: 0 } as SharedValue<number>,
  scaleValue: () => {
    throw new Error('scaleValue is not defined');
  },
  unscaleValue: () => {
    throw new Error('unscaleValue is not defined');
  },
  scaleObject: () => {
    throw new Error('scaleObject is not defined');
  },
  unscaleObject: () => {
    throw new Error('unscaleObject is not defined');
  },
});

/**
 * Custom hook that provides access to the ResponsiveScaleContext
 *
 * Usage example:
 * ```
 * const { responsiveScale, setResponsiveScale } = useResponsiveScale();
 * // Now you can use responsiveScale in your component
 * // or call setResponsiveScale to update it
 * ```
 *
 * @returns The current responsive scale context value
 */
export const useResponsiveScale = () => {
  return useContext(ResponsiveScaleContext);
};
